// Estimated word timing (NARRATION.md "Word timing", "Estimate"): an exact port of tools/word_timing.py `align`.
// Offsets are UTF-16 (JavaScript string indexes), like the apps.

export const FRAME = 0.01;
const MIN_PAUSE_FRAMES = 6;
export const SENTENCE = 3, CLAUSE = 2;
const REL_SIGMA = 0.4, MIN_SIGMA = 0.35;
const MAX_SKIP_BOUNDS = 4, MAX_SKIP_PAUSES = 20;

export type Word = [start: number, end: number, token: string];

export function wordsOf(text: string): Word[] {
  const out: Word[] = [];
  for (const m of text.matchAll(/\S+/g)) out.push([m.index!, m.index! + m[0].length, m[0]]);
  return out;
}

export function syllables(token: string): number {
  const w = token.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 1;
  let n = (w.match(/[aeiouy]+/g) ?? []).length;
  if (w.endsWith("e") && !(w.endsWith("le") || w.endsWith("ee")) && n > 1) n -= 1;
  return Math.max(1, n);
}

export const weight = (token: string) => 1 + 2 * syllables(token);

export function strength(text: string, word: Word, nextStart: number): number {
  const token = word[2].replace(/["'”’)\]»]+$/u, "");
  const gap = text.slice(word[1], nextStart);
  if (gap.includes("\n") || /[.!?…]$/.test(token)) return SENTENCE;
  if (/[,;:—–]$/.test(token) || token === "—" || token === "–") return CLAUSE;
  return 0;
}

/** RMS per 10 ms frame of 24 kHz s16le mono PCM. */
export function frameRMS(pcm: Int16Array, rate = 24000): number[] {
  const n = Math.round(rate * FRAME);
  const out: number[] = [];
  const frames = Math.floor(pcm.length / n);
  for (let f = 0; f < frames; f++) {
    let s = 0;
    for (let i = f * n; i < (f + 1) * n; i++) s += pcm[i] * pcm[i];
    out.push(Math.sqrt(s / n));
  }
  return out;
}

export function pausesOf(rms: number[]): { runs: [number, number][]; first: number; last: number } {
  if (!rms.length) return { runs: [], first: 0, last: 0 };
  const s = [...rms].sort((a, b) => a - b);
  const floor = s[Math.floor(s.length * 0.05)];
  const loud = s[Math.floor(s.length * 0.7)];
  const threshold = floor + 0.14 * (loud - floor);
  const quiet = rms.map((v) => v <= threshold);
  let first = quiet.findIndex((q) => !q);
  if (first < 0) first = 0;
  let fromEnd = 0;
  for (let i = quiet.length - 1; i >= 0; i--) { if (!quiet[i]) { fromEnd = quiet.length - 1 - i; break; } if (i === 0) fromEnd = 0; }
  const anyLoud = quiet.some((q) => !q);
  const last = quiet.length - (anyLoud ? fromEnd : 0);
  const runs: [number, number][] = [];
  let i = first;
  while (i < last) {
    if (quiet[i]) {
      let j = i;
      while (j < last && quiet[j]) j++;
      if (j - i >= MIN_PAUSE_FRAMES) runs.push([i, j]);
      i = j;
    } else i++;
  }
  return { runs, first, last };
}

const r3 = (x: number) => Math.round(x * 1000) / 1000;

/** Word timings [start, end] in seconds for one piece. */
export function align(text: string, rms: number[]): [number, number][] {
  const words = wordsOf(text);
  if (!words.length) return [];
  const { runs, first, last } = pausesOf(rms);
  const t0 = first * FRAME, t1 = Math.max(first + 1, last) * FRAME;
  const weights = words.map((w) => weight(w[2]));
  const cum = [0];
  for (const w of weights) cum.push(cum[cum.length - 1] + w);
  const pauses = runs.map(([a, b]) => [a * FRAME, b * FRAME] as [number, number]);
  const plen = pauses.map(([a, b]) => b - a);
  const plenCum = [0], pskipCum = [0];
  for (const l of plen) {
    plenCum.push(plenCum[plenCum.length - 1] + l);
    pskipCum.push(pskipCum[pskipCum.length - 1] + 0.15 * Math.min(1, l / 0.3));
  }
  const speechPerWeight = Math.max(0.1, (t1 - t0) - plenCum[plenCum.length - 1]) / cum[cum.length - 1];
  const bounds: [number, number][] = [];
  for (let k = 0; k < words.length - 1; k++) {
    const st = strength(text, words[k], words[k + 1][0]);
    if (st) bounds.push([k, st]);
  }
  const bskipCum = [0];
  for (const [, st] of bounds) bskipCum.push(bskipCum[bskipCum.length - 1] + (st === SENTENCE ? 0.4 : 0.1));
  const K = bounds.length, P = pauses.length;

  const spanCost = (prevWord: number, word: number, prevPause: number, nextPauseIndex: number, startT: number, endT: number) => {
    const skipped = plenCum[nextPauseIndex] - plenCum[prevPause + 1];
    const actual = Math.max(0, endT - startT - skipped);
    const expected = (cum[word + 1] - cum[prevWord + 1]) * speechPerWeight;
    const sigma = Math.max(MIN_SIGMA, REL_SIGMA * expected);
    return ((actual - expected) / sigma) ** 2;
  };
  const skips = (pb: number, bi: number, pp: number, pi: number) =>
    (bskipCum[bi] - bskipCum[pb + 1]) + (pskipCum[pi] - pskipCum[pp + 1]);

  const key = (b: number, p: number) => b * 100000 + p; // b, p ≥ -1
  const score = new Map<number, number>([[key(-1, -1), 0]]);
  const nodes: [number, number][] = [[-1, -1]];
  const back = new Map<number, [number, number]>();
  for (let bi = 0; bi < K; bi++) {
    for (let pi = 0; pi < P; pi++) {
      let best: number | null = null, arg: [number, number] | null = null;
      for (let pb = Math.max(-1, bi - MAX_SKIP_BOUNDS - 1); pb < bi; pb++) {
        for (let pp = Math.max(-1, pi - MAX_SKIP_PAUSES - 1); pp < pi; pp++) {
          const sc = score.get(key(pb, pp));
          if (sc === undefined || (pb === -1) !== (pp === -1)) continue;
          const prevEnd = pp === -1 ? t0 : pauses[pp][1];
          const prevWord = pb === -1 ? -1 : bounds[pb][0];
          const s = sc + (bounds[bi][1] === SENTENCE ? 1.0 : 0.6) + Math.min(1, plen[pi] / 0.25)
            - skips(pb, bi, pp, pi) - spanCost(prevWord, bounds[bi][0], pp, pi, prevEnd, pauses[pi][0]);
          if (best === null || s > best) { best = s; arg = [pb, pp]; }
        }
      }
      if (arg !== null) { score.set(key(bi, pi), best!); back.set(key(bi, pi), arg); nodes.push([bi, pi]); }
    }
  }
  let best: number | null = null, arg: [number, number] | null = null;
  for (const [pb, pp] of nodes) {
    const sc = score.get(key(pb, pp))!;
    if (pb !== -1 && (K - 1 - pb > MAX_SKIP_BOUNDS || P - 1 - pp > MAX_SKIP_PAUSES)) continue;
    const prevEnd = pp === -1 ? t0 : pauses[pp][1];
    const prevWord = pb === -1 ? -1 : bounds[pb][0];
    const s = sc - skips(pb, K, pp, P) - spanCost(prevWord, words.length - 1, pp, P, prevEnd, t1);
    if (best === null || s > best) { best = s; arg = [pb, pp]; }
  }
  const matches: [number, [number, number]][] = [];
  let node = arg;
  while (node && !(node[0] === -1 && node[1] === -1)) {
    matches.push([bounds[node[0]][0], pauses[node[1]]]);
    node = back.get(key(node[0], node[1])) ?? null;
  }
  matches.reverse();
  const spans: [number, number, number, number][] = [];
  let startWord = 0, startTime = t0;
  for (const [k, [ps, pe]] of matches) {
    spans.push([startWord, k, startTime, ps]);
    startWord = k + 1; startTime = pe;
  }
  spans.push([startWord, words.length - 1, startTime, t1]);
  const out: [number, number][] = new Array(words.length);
  for (const [a, b, ta, tb] of spans) {
    let total = 0;
    for (let i = a; i <= b; i++) total += weights[i];
    let t = ta;
    for (let i = a; i <= b; i++) {
      const d = total ? (tb - ta) * weights[i] / total : 0;
      out[i] = [r3(t), r3(t + d)];
      t += d;
    }
  }
  return out;
}

/** A part's timing map (NARRATION.md): [startChar, endChar, startSeconds, endSeconds] per word. */
export interface TimingMap { version: 2; source: "estimate"; words: [number, number, number, number][] }

/** Pieces in plan order → the part's map: each piece's words take the next n words of the part. */
export function partTiming(partText: string, pieces: { text: string; pcm: Int16Array }[], rate = 24000): TimingMap {
  const words = wordsOf(partText);
  const out: [number, number, number, number][] = [];
  let start = 0, w = 0;
  for (const piece of pieces) {
    const times = align(piece.text, frameRMS(piece.pcm, rate));
    for (const [a, b] of times) {
      if (w >= words.length) break;
      out.push([words[w][0], words[w][1], r3(start + a), r3(start + b)]);
      w++;
    }
    start += piece.pcm.length / rate;
  }
  // Words a piece didn't cover (shouldn't happen) end at the last time.
  const end = out.length ? out[out.length - 1][3] : 0;
  while (w < words.length) { out.push([words[w][0], words[w][1], end, end]); w++; }
  return { version: 2, source: "estimate", words: out };
}

/** The last word whose start ≤ time. */
export function wordAt(map: TimingMap, t: number): number | null {
  const ws = map.words;
  if (!ws.length) return null;
  let lo = 0, hi = ws.length - 1, found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ws[mid][2] <= t) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found < 0 ? 0 : found;
}
